// accountHelpers.ts

import { Account } from "../interfaces/AccountData.ts"; // Adjust the path as needed

// Helper function to parse TimeSpan string (HH:mm:ss or D.HH:mm:ss) to hours
const parseTimeSpanToHours = (timeSpan: string): number => {
    try {
        if (!timeSpan) return 24;

        let days = 0;
        let timeString = timeSpan;

        // Check if it has the days.hours:minutes:seconds format (e.g., "1.00:00:00")
        if (timeString.includes('.')) {
            const daysPart = timeString.split('.');
            days = parseInt(daysPart[0], 10) || 0;
            timeString = daysPart[1];
        }

        const parts = timeString.split(':');
        if (parts.length >= 2) {
            const hours = parseInt(parts[0], 10) || 0;
            const minutes = parseInt(parts[1], 10) || 0;
            return (days * 24) + hours + (minutes / 60);
        }
        return 24; // Default fallback
    } catch (error) {
        console.error('Error parsing TimeSpan:', timeSpan, error);
        return 24; // Default fallback
    }
};

// Helper function to convert hours to TimeSpan string (HH:mm:ss)
const hoursToTimeSpan = (hours: number): string => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
};

/**
 * Converts the composite API data (with nested account, settings, and discordChannelSettings)
 * into our flattened Account interface.
 */
export const mapCompositeToAccount = (composite: any): Account => {
    const { account, settings, discordChannelSettings } = composite;
    console.log(account);
    return {
        id: account.id.toString(),
        name: account.name,
        type: account.isGlobal ? "Shared" : "Personal",
        isActive: account.active,
        isTestnet: account.isTestnet,
        apiKey: account.apiKey,
        apiSecret: account.apiSecret,
        ownerUserId: account.ownerUserId,
        isGlobalAccount: account.isGlobal,
        fullSizeTradePercentage: settings ? settings.fullSizeTradePercentage : 0,
        lottoTradePercentage: settings ? settings.lottoTradePercentage : 0,
        sizeMarginOfErrorPercentage: settings ? settings.sizeMarginOfErrorPercentage : 0,
        weeklyAllocation: settings ? settings.weeklyAllocation : 0,
        opraFeedEnabled: settings ? settings.opraFeedEnabled : false,
        sellsDisabled: settings ? settings.sellsDisabled : false,
        fullSizeSellEnabled: settings ? settings.fullSizeSellEnabled : false,
        sameDayExpirationLottoEnabled: settings ? settings.sameDayExpirationLottoEnabled : false,
        balanceTrackingEnabled: settings ? settings.balanceTrackingEnabled : false,
        preMarketClosePositionClearEnabled: settings ? settings.preMarketClosePositionClearEnabled : false,
        profitTriggerPercentage: settings ? settings.profitTriggerPercentage : undefined,
        lossTriggerPercentage: settings ? settings.lossTriggerPercentage : undefined,
        balanceTrackingTriggerTimeoutHours: settings && settings.balanceTrackingTriggerTimeout
            ? parseTimeSpanToHours(settings.balanceTrackingTriggerTimeout) : undefined,
        willUnfreezeAt: settings ? settings.willUnfreezeAt : undefined,
        marketOpenBalance: settings ? settings.marketOpenBalance : undefined,
        discountedBuyPercentage: settings ? settings.discountedBuyPercentage : undefined,
        vixPauseEnabled: settings ? settings.vixPauseEnabled : false,
        vixPauseThreshold: settings ? settings.vixPauseThreshold : undefined,
        vvixPauseThreshold: settings ? settings.vvixPauseThreshold : undefined,
        vixPauseLiveMode: settings ? settings.vixPauseLiveMode : false,
        vixExitAllEnabled: settings ? settings.vixExitAllEnabled : false,
        vixExitAllThreshold: settings ? settings.vixExitAllThreshold : undefined,
        vvixExitAllThreshold: settings ? settings.vvixExitAllThreshold : undefined,
        ignorePuts: settings ? settings.ignorePuts : false,
        discordChannels: discordChannelSettings.map((dcs: any) => ({
            id: dcs.id.toString(),
            channelId: dcs.discordChannelId,
            enabled: dcs.isEnabled,
            tradeSizePercentage: dcs.tradeSizeMultiplier,
        })),
        trades: [] // Assuming trades are not provided by the composite endpoint.
    };
};

/**
 * Converts our flattened Account into the composite structure expected by the API when saving.
 */
export const mapAccountToComposite = (account: Account) => {
    return {
        account: {
            id: parseInt(account.id) || 0, // For new accounts, this might be 0 or omitted.
            name: account.name,
            active: account.isActive,
            apiKey: account.apiKey,
            apiSecret: account.apiSecret,
            ownerUserId: account.ownerUserId,
            isGlobal: account.isGlobalAccount,
            isTestnet: account.isTestnet,
        },
        settings: {
            accountId: parseInt(account.id) || 0,
            weeklyAllocation: account.weeklyAllocation,
            fullSizeTradePercentage: account.fullSizeTradePercentage,
            lottoTradePercentage: account.lottoTradePercentage,
            sizeMarginOfErrorPercentage: account.sizeMarginOfErrorPercentage,
            opraFeedEnabled: account.opraFeedEnabled,
            sellsDisabled: account.sellsDisabled,
            fullSizeSellEnabled: account.fullSizeSellEnabled,
            sameDayExpirationLottoEnabled: account.sameDayExpirationLottoEnabled,
            balanceTrackingEnabled: account.balanceTrackingEnabled,
            preMarketClosePositionClearEnabled: account.preMarketClosePositionClearEnabled,
            profitTriggerPercentage: account.profitTriggerPercentage,
            lossTriggerPercentage: account.lossTriggerPercentage,
            balanceTrackingTriggerTimeout: account.balanceTrackingTriggerTimeoutHours
                ? hoursToTimeSpan(account.balanceTrackingTriggerTimeoutHours) : undefined,
            discountedBuyPercentage: account.discountedBuyPercentage,
            vixPauseEnabled: account.vixPauseEnabled,
            vixPauseThreshold: account.vixPauseThreshold,
            vvixPauseThreshold: account.vvixPauseThreshold,
            vixPauseLiveMode: account.vixPauseLiveMode,
            vixExitAllEnabled: account.vixExitAllEnabled,
            vixExitAllThreshold: account.vixExitAllThreshold,
            vvixExitAllThreshold: account.vvixExitAllThreshold,
            ignorePuts: account.ignorePuts,
        },
        discordChannelSettings: account.discordChannels.map((dc) => ({
            id: parseInt(dc.id) || 0,
            accountId: parseInt(account.id) || 0,
            discordChannelId: dc.channelId,
            isEnabled: dc.enabled,
            tradeSizeMultiplier: dc.tradeSizePercentage,
        })),
    };
};
